import { useState, useEffect } from 'react';

/**
 * useHashRouter
 *
 * Simple hash-based routing for the PWA.
 * Uses window.location.hash to determine the current route.
 *
 * Routes:
 * - (empty / #/)       -> Landing page (public)
 * - #/app              -> Main app (authenticated experience)
 * - #/share/:shareId   -> Shared summary page (public, no auth)
 * - #/join/:groupId    -> Join flow from shared page CTA
 *
 * Why hash routing?
 * - Works with static hosting (no server config needed)
 * - Simple to implement without react-router
 * - Good enough for MVP/pilot
 *
 * For production, consider migrating to react-router with proper
 * server-side routing support.
 */

export function useHashRouter() {
  const [route, setRoute] = useState(() => parseHash(window.location.hash));
  
  useEffect(() => {
    const handleHashChange = () => {
      setRoute(parseHash(window.location.hash));
    };
    
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);
  
  return route;
}

function parseHash(hash) {
  // Remove leading # if present
  const raw = hash.replace(/^#/, '') || '/';

  // Split path from query string (e.g., /join?group=abc → /join + group=abc)
  const [path, queryString] = raw.split('?');
  const params = {};
  if (queryString) {
    queryString.split('&').forEach(pair => {
      const [key, val] = pair.split('=');
      if (key) params[decodeURIComponent(key)] = decodeURIComponent(val || '');
    });
  }

  // Match /share/:shareId
  const shareMatch = path.match(/^\/share\/([a-zA-Z0-9_-]+)$/);
  if (shareMatch) {
    return {
      page: 'share',
      shareId: shareMatch[1]
    };
  }

  // Match /providers -> provider information page
  if (path === '/providers') {
    return { page: 'providers' };
  }

  // Match /privacy -> privacy policy page
  if (path === '/privacy') {
    return { page: 'privacy' };
  }

  // Match /terms -> terms of service page
  if (path === '/terms') {
    return { page: 'terms' };
  }

  // Match /get -> smart app-download link (QR code target on clinic cards)
  if (path === '/get') {
    return { page: 'get' };
  }

  // Match /setup -> pre-visit onboarding
  if (path === '/setup') {
    return { page: 'setup' };
  }

  // Match /app -> main app
  if (path === '/app' || path.startsWith('/app/')) {
    return { page: 'app' };
  }

  // Match /join or /join/:segment or /join?group=:groupId
  if (path === '/join') {
    return { page: 'join', groupId: params.group || null };
  }
  const joinMatch = path.match(/^\/join\/([a-zA-Z0-9_-]+)$/);
  if (joinMatch) {
    const segment = joinMatch[1];
    // Invite tokens (from invitation emails) are 'iv' + hex — they pre-fill
    // the invited email and accept the invite after sign-in. Anything else
    // is a group ID (the older /join/:groupId form).
    if (/^iv[0-9a-f]{16,}$/.test(segment)) {
      return { page: 'join', inviteToken: segment, groupId: null };
    }
    return {
      page: 'join',
      groupId: segment
    };
  }

  // No hash route matched. Check for a path-based share URL
  // (clarime.health/share/<token> — the Universal Links format; the hash
  // form #/share/<token> above still works for links sent before Session 78).
  // Hash routes take precedence so in-page navigation (e.g. tapping
  // "join on the web" from a share page) can leave the share view even
  // though the pathname stays /share/<token>.
  const pathShareMatch = window.location.pathname.match(/^\/share\/([a-zA-Z0-9_-]+)\/?$/);
  if (pathShareMatch) {
    return {
      page: 'share',
      shareId: pathShareMatch[1]
    };
  }

  // Default: landing page
  return {
    page: 'home'
  };
}

// Navigate programmatically
export function navigateTo(path) {
  window.location.hash = path;
}

// Generate share URL (path-based so iOS Universal Links can match it)
export function generateShareUrl(shareId) {
  return `${window.location.origin}/share/${shareId}`;
}
